/**
 * 法律页面文案（OPT-054）
 *
 * 隐私政策 / 服务条款的内容单一数据源。
 * - 文案骨架改编自 GitHub site-policy 仓库（CC0-1.0 授权，可自由复制修改无需署名），
 *   已裁掉 GitHub 特有条款（仓库/Copilot/API/DMCA 等），并填入我方事实
 *   （Cloudflare D1 存储、Gmail 发信、Google OAuth、Creem 收款、CSV 导出、删除走人工）
 * - 只做 en + zh-TW 两版（拍板：模板先英文，繁中对照），其余语言回退英文
 * - 不进 shared/i18n 五语言字典（避免触发 key 一致性测试的 5 语言全文翻译负担）
 * - 本模板仅供参考、不构成法律意见；收款上线前建议法务复核
 */

export interface LegalSection {
  heading: string
  paragraphs: string[]
}

export interface LegalDoc {
  title: string
  lastUpdated: string
  intro: string
  sections: LegalSection[]
}

export type LegalKind = 'privacy' | 'terms'

export const CONTACT_EMAIL = 'hi@klikpt.vip'

const privacyEn: LegalDoc = {
  title: 'Privacy Policy',
  lastUpdated: 'September 1, 2026',
  intro:
    'LOTICNATURE PT ("we", "our") operates the web application at app.klikpt.vip, a scheduling and package-tracking tool for personal trainers. This Privacy Policy explains what personal data we collect, how we use it, who we share it with, where it is stored, and the rights you have over your data.',
  sections: [
    {
      heading: '1. Information We Collect',
      paragraphs: [
        'From you — Account data: your email address, name, language preference, and password (stored only as a bcrypt hash — we never store plain-text passwords).',
        'From you — Coaching data: client (student) names, contact details, notes, session packages, appointments, and preset locations that you enter into the service. This data belongs to you.',
        'From third parties — Google sign-in: if you choose "Sign in with Google", we receive your Google account\'s email address, name, and profile identifier, used solely to authenticate you. What Google shares is governed by your Google settings and Google\'s privacy policy.',
        'Automatically — Usage data: activity logs of actions you perform in the app (e.g. sign-ins, schedule changes), plus standard technical data such as IP address, device/browser type, and timestamps, used for security auditing and service operation.',
        'Automatically — Cookies: we use essential cookies only (authentication tokens and a short-lived OAuth state cookie). See the Cookies section below.',
      ],
    },
    {
      heading: '2. How We Use Your Information',
      paragraphs: [
        'Service provision: to deliver and operate the service as you configure it — scheduling, package tracking, client management, and notifications.',
        'Communication: to send transactional email — email verification, password reset, and appointment reminders — through our official Gmail channel.',
        'Safety and security: rate limiting, abuse prevention, and error diagnosis to keep the service reliable and secure.',
        'Product improvement: to fix defects and keep the service up to date and performant. We apply data minimization and use the minimum amount of personal data required.',
        'We do not sell your personal data, and we do not use your coaching data for advertising.',
      ],
    },
    {
      heading: '3. Sharing of Personal Data',
      paragraphs: [
        'Subprocessors and service providers: we use vendors to provide the service on our behalf, bound by contractual obligations to protect your information:',
        'Cloudflare — hosting, content delivery, and database infrastructure (Pages, D1).',
        'Google — OAuth sign-in and the Gmail API used to send transactional email.',
        'Creem — payment processing for paid subscriptions. Card numbers are handled entirely by the payment processor; we never see or store them.',
        'Competent authorities: we may disclose personal data to authorized law enforcement, regulators, or courts in response to lawful requests or to protect our rights and safety.',
        'Corporate transactions: if we are involved in a merger, sale, or acquisition, personal data may be transferred within the limits of the law and in accordance with this policy.',
        'We do not share your personal data with advertisers or data brokers.',
      ],
    },
    {
      heading: '4. Where Your Data Is Stored (International Transfers)',
      paragraphs: [
        'The service is hosted on Cloudflare (Pages + D1 database). Data is processed and stored on Cloudflare\'s network, primarily in United States data centers, and may be cached at edge locations worldwide for performance.',
        'This means personal data may be transferred to and processed in countries other than your own, including countries that may not have been recognized as providing an adequate level of data protection. Where required, such transfers rely on the safeguards provided by our subprocessors (e.g. Cloudflare\'s standard contractual clauses).',
      ],
    },
    {
      heading: '5. Your Privacy Rights',
      paragraphs: [
        'Depending on your residence location, you may have legal rights regarding your personal data, including: the right to access the data we hold about you; the right to rectify inaccurate data; the right to erase or limit processing; the right to object to processing; the right to withdraw consent; and the right to data portability.',
        'Export: you can export any client\'s data as a CSV file from within the app at any time (data portability).',
        'Deletion: account deletion is currently handled manually — email us at ' + CONTACT_EMAIL + ' from your registered address and we will delete your account and associated personal data.',
        'Access & correction: most data can be viewed and edited directly in the app. For anything else, or to exercise any other right, contact us at ' + CONTACT_EMAIL + '. To verify your identity for security, we may request additional information before addressing your request.',
        'Depending on your region, you also have the right to lodge a complaint with your local data protection authority.',
      ],
    },
    {
      heading: '6. Cookies & Local Storage',
      paragraphs: [
        'We use HttpOnly cookies for authentication (access/refresh tokens and a short-lived OAuth state cookie) and browser local storage for display preferences such as language. These are essential to providing the service.',
        'We do not use advertising, analytics, or cross-site tracking cookies.',
      ],
    },
    {
      heading: '7. Security & Retention',
      paragraphs: [
        'We use appropriate administrative and technical security controls to protect your personal data: HTTPS in transit, hashed credential storage, short-lived tokens, and rate limiting.',
        'We retain your personal data as long as your account is active and as needed to fulfill contractual obligations, comply with legal requirements, resolve disputes, and enforce agreements. When you request deletion, we remove your account and associated personal data from our production systems; some information may remain in encrypted backups for a limited period.',
      ],
    },
    {
      heading: '8. Information for Minors',
      paragraphs: [
        'The service is intended for professional personal trainers and is not directed at individuals under the age of 13. We do not knowingly collect personal data directly from children under 13. If you become aware that a minor has provided us with personal data, please contact us at ' + CONTACT_EMAIL + '.',
        'Trainers may enter data about their own clients, including minors; in that case the trainer is the party responsible for having the right to collect and store that data (see our Terms of Service).',
      ],
    },
    {
      heading: '9. Changes to This Policy',
      paragraphs: [
        'We may update this policy from time to time. If we make material changes, we will update the "last updated" date above and, where appropriate, notify you by email or in-app notice before the change takes effect.',
      ],
    },
    {
      heading: '10. Contact',
      paragraphs: [
        'Questions about this policy or your personal data: ' + CONTACT_EMAIL,
      ],
    },
  ],
}

const termsEn: LegalDoc = {
  title: 'Terms of Service',
  lastUpdated: 'September 1, 2026',
  intro:
    'These Terms of Service (the "Terms") govern your use of LOTICNATURE PT (app.klikpt.vip), a scheduling and package-tracking tool for independent personal trainers. By creating an account or using the service, you agree to these Terms together with our Privacy Policy.',
  sections: [
    {
      heading: '1. Definitions',
      paragraphs: [
        '"Account" means your legal relationship with us and your authorization to log in to and use the service.',
        '"Service" means the LOTICNATURE PT web application and related features provided at app.klikpt.vip.',
        '"Your Content" means the data you upload, submit, or create through the service — client records, appointments, notes, packages, and locations.',
        '"User", "you" means the individual person using the service. You must be at least 13 years of age; if your country sets a higher minimum age, you are responsible for complying with it.',
      ],
    },
    {
      heading: '2. Account Terms',
      paragraphs: [
        'Required information: you must provide a valid email address to complete signup. Additional information may be required for billing if you subscribe to a paid plan.',
        'Account requirements: you must be a human — accounts registered by bots or other automated methods are not permitted. Your login may only be used by one person; a single login may not be shared.',
        'Account security: you are responsible for keeping your credentials confidential and for all activity that occurs under your account. Notify us promptly at ' + CONTACT_EMAIL + ' if you become aware of any unauthorized use of your account.',
        'You may not use the service in violation of applicable laws, including export control or sanctions laws.',
      ],
    },
    {
      heading: '3. Acceptable Use',
      paragraphs: [
        'Your use of the service must not violate any applicable laws or regulations. You agree that you will not: use the service for unlawful purposes; attempt to access other users\' accounts or data; scrape, crawl, or overload the service; probe or test its security; or otherwise interfere with its operation.',
        'We may refuse or remove content and suspend accounts that violate these Terms or applicable law.',
      ],
    },
    {
      heading: '4. Your Content',
      paragraphs: [
        'Responsibility: you are responsible for Your Content and any harm resulting from it. You are responsible for having the right to collect and store your clients\' personal data, and for complying with the privacy laws that apply to you and your clients.',
        'Ownership: you retain ownership of Your Content. These Terms do not grant us any ownership of it.',
        'License grant to us: you grant us the right to store, host, back up, and process Your Content solely as necessary to provide, maintain, and improve the service. This license ends when you delete Your Content or your account, except as retention is described in our Privacy Policy.',
        'Removal: we may remove content that violates applicable law or these Terms.',
      ],
    },
    {
      heading: '5. Plans, Billing & Refunds',
      paragraphs: [
        'Pricing: the Free plan (L0) is free of charge. The Pro plan (L1) is USD $9.99 per month. Plan features and limits are described on the pricing page; prices are subject to change with at least 30 days\' notice before the end of a payment term.',
        'Billing: subscriptions to paid plans are billed in advance on a monthly basis and renew automatically until cancelled. We may grant complimentary or promotional access to paid features at our discretion for a limited period.',
        'No refunds for partial periods: payments are non-refundable and there will be no refunds or credits for partial months; the service remains active for the length of the paid billing period. For exceptional cases, contact us at ' + CONTACT_EMAIL + ' — we review requests case by case.',
        'Responsibility for payment: you are responsible for all fees, including applicable taxes, associated with your use of the service. Free accounts are not required to provide payment information.',
      ],
    },
    {
      heading: '6. Cancellation and Termination',
      paragraphs: [
        'Cancellation by you: you may stop using the service at any time. Account deletion is currently handled manually — email us at ' + CONTACT_EMAIL + ' from your registered address. Upon cancellation, we will delete your account and associated data as described in the Privacy Policy; this cannot be recovered once deleted. Before cancelling, you may export your clients\' data as CSV from within the app.',
        'Termination by us: we may suspend or terminate your access to all or part of the service at any time, with or without cause or notice — for example, for breach of these Terms.',
        'Survival: provisions that by their nature should survive termination do survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.',
      ],
    },
    {
      heading: '7. Communications',
      paragraphs: [
        'Electronic communication: for contractual purposes, you consent to receive communications from us in electronic form (via your account email or in the service), and agree that electronic notices satisfy any legal requirement that such communications be in writing.',
        'We offer support by email only; we do not offer telephone support.',
      ],
    },
    {
      heading: '8. Disclaimer of Warranties',
      paragraphs: [
        'The service is an administrative tool only. It does not provide medical, fitness, nutritional, or other professional advice, and it is not a substitute for professional judgment in your coaching practice.',
        'We provide the service "as is" and "as available", without warranty of any kind. Without limiting this, we expressly disclaim all warranties, whether express, implied, or statutory, including any warranty of merchantability, fitness for a particular purpose, title, security, accuracy, and non-infringement.',
        'We do not warrant that the service will meet your requirements, or that it will be uninterrupted, timely, secure, or error-free. You assume full responsibility and risk of loss resulting from your use of information obtained from the service.',
      ],
    },
    {
      heading: '9. Limitation of Liability',
      paragraphs: [
        'To the maximum extent permitted by law, we will not be liable to you or any third party for any loss of profits, use, goodwill, or data, or for any incidental, indirect, special, consequential, or exemplary damages arising from: your use or inability to use the service; any modification, price change, suspension, or discontinuance of the service; unauthorized access to or alteration of your transmissions or data; statements or conduct of any third party; or any other matter relating to the service.',
        'Our aggregate liability is limited to the amount you paid us in the 12 months preceding the claim (or USD 100 if you have not paid us anything). We will have no liability for any failure or delay due to matters beyond our reasonable control.',
      ],
    },
    {
      heading: '10. Changes to These Terms',
      paragraphs: [
        'We may amend these Terms at any time and will update this page accordingly. We will notify you of material changes — such as price increases — at least 30 days before they take effect, by email or in-app notice. Your continued use of the service after the effective date constitutes acceptance of the revised Terms.',
        'We may modify or discontinue the service (or any part of it), temporarily or permanently, with or without notice.',
      ],
    },
    {
      heading: '11. Miscellaneous & Contact',
      paragraphs: [
        'Severability: if any part of these Terms is held invalid or unenforceable, that portion will be construed to reflect the parties\' original intent, and the remaining portions remain in full force. A failure to enforce any provision is not a waiver of our right to enforce it later.',
        'Entire agreement: these Terms, together with the Privacy Policy, are the complete agreement between you and us regarding the service.',
        'Questions about these Terms: ' + CONTACT_EMAIL,
      ],
    },
  ],
}

const privacyZhTw: LegalDoc = {
  title: '隱私政策',
  lastUpdated: '2026 年 9 月 1 日',
  intro:
    'LOTICNATURE PT（下稱「我們」）營運 app.klikpt.vip 網路應用程式——一套為私人教練打造的排課與課時追蹤工具。本隱私政策說明我們收集哪些個人資料、如何使用、與誰分享、儲存於何處，以及您對自己資料享有的權利。',
  sections: [
    {
      heading: '1. 我們收集的資訊',
      paragraphs: [
        '由您提供——帳號資料：您的電子郵件地址、姓名、語言偏好，以及密碼（僅以 bcrypt 雜湊形式儲存——我們絕不儲存明文密碼）。',
        '由您提供——教學資料：您輸入服務的學員姓名、聯絡方式、備註、課時套餐、預約與預設地點。這些資料屬於您。',
        '來自第三方——Google 登入：若您選擇「使用 Google 登入」，我們會接收您 Google 帳號的電子郵件地址、姓名與個人資料識別碼，僅用於身分驗證。Google 分享的範圍取決於您的 Google 設定與 Google 隱私政策。',
        '自動收集——使用資料：您在應用程式中執行的操作日誌（如登入、排課變更），以及 IP 位址、裝置／瀏覽器類型、時間戳記等標準技術資料，用於安全稽核與服務營運。',
        '自動收集——Cookie：我們僅使用必要性 Cookie（身分驗證權杖及短期的 OAuth 狀態 Cookie），詳見下方 Cookie 章節。',
      ],
    },
    {
      heading: '2. 我們如何使用您的資訊',
      paragraphs: [
        '提供服務：依您的設定交付並營運本服務——排課、課時追蹤、學員管理與通知。',
        '通訊聯絡：透過我們的官方 Gmail 通道發送交易性郵件——信箱驗證、密碼重設與預約提醒。',
        '安全防護：速率限制、濫用防範與錯誤診斷，以維持服務的可靠性與安全性。',
        '產品改進：修正缺陷並維持服務的更新與效能。我們遵循資料最小化原則，僅使用所需的最少量個人資料。',
        '我們不出售您的個人資料，也不將您的教學資料用於廣告。',
      ],
    },
    {
      heading: '3. 個人資料的分享',
      paragraphs: [
        '次處理者與服務供應商：我們委託下列廠商代為提供服務，其受合約義務約束以保護您的資訊：',
        'Cloudflare——託管、內容傳遞與資料庫基礎設施（Pages、D1）。',
        'Google——OAuth 登入，以及用於發送交易性郵件的 Gmail API。',
        'Creem——付費訂閱的付款處理。信用卡號完全由支付處理商處理；我們不會看到或儲存卡號。',
        '主管機關：因應合法要求或為保護我們的權利與安全，我們可能向經授權的執法機關、監管機構或法院揭露個人資料。',
        '企業交易：若我們涉及合併、出售或收購，個人資料可能在法律允許範圍內並依本政策移轉。',
        '我們不會將您的個人資料分享給廣告商或資料經紀商。',
      ],
    },
    {
      heading: '4. 資料儲存位置（國際傳輸）',
      paragraphs: [
        '本服務託管於 Cloudflare（Pages + D1 資料庫）。資料在 Cloudflare 網路上處理與儲存，主要位於美國資料中心，並可能為提升效能而在全球邊緣節點暫存。',
        '這表示個人資料可能傳輸至您所在國家以外之地區處理，包括未被認定具備適足資料保護水準的國家。於必要時，此類傳輸仰賴次處理者提供的保障措施（例如 Cloudflare 的標準契約條款）。',
      ],
    },
    {
      heading: '5. 您的隱私權利',
      paragraphs: [
        '依您的居住地，您可能對個人資料享有法定權利，包括：存取我們持有的資料之權利；更正不正確資料之權利；刪除或限制處理之權利；反對處理之權利；撤回同意之權利；以及資料可攜權。',
        '匯出：您可隨時在應用程式內將任一學員的資料匯出為 CSV 檔案（資料可攜權）。',
        '刪除：帳號刪除目前採人工處理——請使用註冊信箱來信 ' + CONTACT_EMAIL + '，我們將刪除您的帳號與相關個人資料。',
        '存取與更正：大部分資料可直接在應用程式內檢視與編輯；其他需求或行使其他權利，請來信 ' + CONTACT_EMAIL + '。為安全起見，處理您的要求前我們可能要求您提供額外資訊以驗證身分。',
        '依您所在地區，您亦有權向當地資料保護主管機關提出申訴。',
      ],
    },
    {
      heading: '6. Cookie 與本機儲存',
      paragraphs: [
        '我們使用 HttpOnly Cookie 進行身分驗證（存取/更新權杖及短期的 OAuth 狀態 Cookie），並以瀏覽器本機儲存保存顯示偏好（如語言）。這些皆為提供服務所必需。',
        '我們不使用廣告、分析或跨站追蹤 Cookie。',
      ],
    },
    {
      heading: '7. 安全與保留',
      paragraphs: [
        '我們採取適當的管理與技術安全控管以保護您的個人資料：傳輸採 HTTPS、憑證以雜湊儲存、短期權杖及速率限制。',
        '帳號有效期間，以及為履行契約義務、遵循法律要求、解決爭議與執行協議所需之期間內，我們會保留您的個人資料。當您提出刪除要求時，我們會從生產系統移除您的帳號與相關個人資料；部分資訊可能在加密備份中保留一段有限時間。',
      ],
    },
    {
      heading: '8. 未成年人資訊',
      paragraphs: [
        '本服務以專業私人教練為對象，不針對未滿 13 歲之人士。我們不會故意直接向未滿 13 歲的兒童收集個人資料。若您發現有未成年人向我們提供個人資料，請來信 ' + CONTACT_EMAIL + '。',
        '教練可能輸入其學員（包括未成年人）的資料；在此情況下，由教練負責確保有權收集並儲存該等資料（詳見我們的服務條款）。',
      ],
    },
    {
      heading: '9. 本政策的變更',
      paragraphs: [
        '我們可能不時更新本政策。若有重大變更，我們將更新上方「最後更新」日期，並視情況於變更生效前以電子郵件或應用程式內通知您。',
      ],
    },
    {
      heading: '10. 聯絡我們',
      paragraphs: [
        '關於本政策或您個人資料的問題：' + CONTACT_EMAIL,
      ],
    },
  ],
}

const termsZhTw: LegalDoc = {
  title: '服務條款',
  lastUpdated: '2026 年 9 月 1 日',
  intro:
    '本服務條款（下稱「本條款」）規範您對 LOTICNATURE PT（app.klikpt.vip）的使用——這是一套為獨立私人教練打造的排課與課時追蹤工具。建立帳號或使用本服務，即表示您同意本條款及我們的隱私政策。',
  sections: [
    {
      heading: '1. 定義',
      paragraphs: [
        '「帳號」指您與我們之間的法律關係，以及您登入並使用本服務的授權。',
        '「服務」指 app.klikpt.vip 提供的 LOTICNATURE PT 網路應用程式及相關功能。',
        '「您的內容」指您透過本服務上傳、提交或建立的資料——學員紀錄、預約、備註、課時套餐與地點。',
        '「使用者／您」指使用本服務的個人。您必須年滿 13 歲；若您所在國家規定較高的最低年齡，您有責任遵守之。',
      ],
    },
    {
      heading: '2. 帳號條款',
      paragraphs: [
        '必要資訊：完成註冊必須提供有效的電子郵件地址。若您訂閱付費方案，可能需提供額外的計費資訊。',
        '帳號要求：您必須是自然人——不允許以機器人或其他自動化方式註冊帳號。您的登入僅限一人使用，單一登入不得多人共用。',
        '帳號安全：您有責任妥善保管登入憑證，並對帳號下發生的所有活動負責。若發現帳號遭未授權使用，請立即來信 ' + CONTACT_EMAIL + ' 通知我們。',
        '您不得違反適用法律（包括出口管制或制裁法規）使用本服務。',
      ],
    },
    {
      heading: '3. 可接受使用',
      paragraphs: [
        '您對本服務的使用不得違反任何適用法律或法規。您同意不會：將本服務用於非法目的；試圖存取其他使用者的帳號或資料；爬取、抓取或使服務過載；探測或測試其安全性；或以其他方式干擾服務的正常運作。',
        '對於違反本條款或適用法律的內容與帳號，我們得拒絕、移除或停權。',
      ],
    },
    {
      heading: '4. 您的內容',
      paragraphs: [
        '責任：您對您的內容及其造成的任何損害負責。您有責任確保自己有權收集並儲存學員的個人資料，並遵守適用於您與學員的隱私法規。',
        '所有權：您對您的內容保留所有權。本條款不賦予我們對其的任何所有權。',
        '對我們的授權：您授權我們儲存、託管、備份並處理您的內容，但僅以提供、維護與改進本服務所必需者為限。當您刪除內容或帳號時，此授權即告終止，惟隱私政策所述之保留情形除外。',
        '移除：我們得移除違反適用法律或本條款的內容。',
      ],
    },
    {
      heading: '5. 方案、計費與退款',
      paragraphs: [
        '定價：免費方案（L0）不收費。Pro 方案（L1）為每月 9.99 美元。各方案功能與上限以定價頁說明為準；價格如有調整，將於當期計費週期結束前至少 30 天通知。',
        '計費：付費方案的訂閱採按月預先計費，並自動續訂直至取消。我們可能不定期提供付費功能的限時免費體驗資格。',
        '部分期間不退款：款項概不退還，不足一個月的部分亦不退款或折抵；服務在已付費的計費期間內維持有效。特殊情形請來信 ' + CONTACT_EMAIL + '，我們將逐案審理。',
        '付款責任：您需負擔使用本服務相關的所有費用（含適用稅捐）。免費帳號無需提供付款資訊。',
      ],
    },
    {
      heading: '6. 取消與終止',
      paragraphs: [
        '由您取消：您可隨時停止使用本服務。帳號刪除目前採人工處理——請使用註冊信箱來信 ' + CONTACT_EMAIL + '。取消後，我們將依隱私政策刪除您的帳號與相關資料；刪除後無法回復。取消前，您可在應用程式內將學員資料匯出為 CSV。',
        '由我們終止：我們得隨時暫停或終止您對全部或部分服務的存取，無論是否有原因或事先通知——例如違反本條款之情形。',
        '存續條款：依其性質應於終止後繼續有效之條款（包括所有權、保證免責、補償與責任限制），於終止後仍然有效。',
      ],
    },
    {
      heading: '7. 通訊聯絡',
      paragraphs: [
        '電子通訊：基於契約目的，您同意以電子形式（透過您的帳號信箱或本服務內）接收我們的通知，並同意電子通知滿足此類通訊須以書面為之的法律要求。',
        '我們僅提供電子郵件支援，不提供電話支援。',
      ],
    },
    {
      heading: '8. 免責聲明',
      paragraphs: [
        '本服務僅為行政管理工具，不提供醫療、健身、營養或其他專業建議，也不能取代您在教學實務中的專業判斷。',
        '本服務依「現狀」及「現有」基礎提供，不作任何種類的保證。在不限制前述規定的前提下，我們明確否認所有明示、默示或法定的保證，包括適銷性、特定用途適用性、所有權、安全性、正確性及不侵權之保證。',
        '我們不保證本服務符合您的需求，亦不保證服務不間斷、即時、安全或無錯誤。對於因使用本服務所取得資訊而產生的損失風險，由您自行承擔。',
      ],
    },
    {
      heading: '9. 責任限制',
      paragraphs: [
        '在法律允許的最大範圍內，對於因下列事項產生的任何利潤、使用、商譽或資料之損失，或任何附帶、間接、特殊、衍生性或懲罰性損害，我們不對您或任何第三方負責：您使用或無法使用本服務；服務的任何修改、價格調整、暫停或終止；未經授權存取或竄改您的傳輸或資料；任何第三方的聲明或行為；或與本服務相關的任何其他事項。',
        '我們的累計賠償責任以您在索賠前 12 個月內支付給我們的金額為上限（若您未曾付費，則為 100 美元）。對於超出我們合理控制範圍之事由所致的遲延或無法履約，我們不負任何責任。',
      ],
    },
    {
      heading: '10. 本條款的變更',
      paragraphs: [
        '我們得隨時修訂本條款並更新本頁面。對於重大變更（如價格調漲），我們將於生效前至少 30 天以電子郵件或應用程式內通知您。生效日後繼續使用本服務，即視為接受修訂後的條款。',
        '我們得隨時修改或停止本服務（或其任何部分），無論暫時或永久、是否事先通知。',
      ],
    },
    {
      heading: '11. 其他事項與聯絡',
      paragraphs: [
        '可分割性：本條款任何部分經認定無效或不可執行時，該部分將以反映雙方原意的方式解釋，其餘部分仍完全有效。我們未執行某一條款，不構成日後放棄執行該條款之權利。',
        '完整合意：本條款連同隱私政策，構成您與我們之間關於本服務的完整協議。',
        '關於本條款的問題：' + CONTACT_EMAIL,
      ],
    },
  ],
}

/** 按界面语言取法律文档：zh-TW 用繁中版，其余一律回退英文（拍板：模板先英文） */
export function getLegalDoc(kind: LegalKind, language: string): LegalDoc {
  const zhTw = language === 'zh-TW'
  if (kind === 'privacy') return zhTw ? privacyZhTw : privacyEn
  return zhTw ? termsZhTw : termsEn
}
